import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';
import ErrorPage from './ErrorPage';

/**
 * `errorElement` wrapper that REPORTS before rendering `<ErrorPage />`.
 *
 * React Router's `errorElement` is an inner error boundary: for any render
 * throw inside a route subtree it wins over the app-level `<ErrorBoundary />`,
 * so that boundary's `componentDidCatch` — the only place we log a component
 * stack and call `Sentry.captureException` — never runs. The result was a
 * silent failure mode: users land on "Something Went Wrong", click Back,
 * navigate to `/`, and the app recovers, leaving no trace anywhere of what
 * actually threw.
 *
 * This is exactly the shape of the first-time-signup provisioning window,
 * where the protected route tree renders for seconds against a session with
 * no bound `accountId`, empty feature flags, and un-hydrated capabilities —
 * long enough on slower hardware for something in that tree to throw.
 *
 * The report is fire-once per mounted error (keyed on the error identity), so
 * a re-render of the error state does not duplicate the Sentry event.
 */
export default function RouteErrorReporter() {
  const error = useRouteError();

  useEffect(() => {
    // A thrown `Response` (the client library rejects with the raw `Response`
    // object) carries no stack and serializes to `{}` — capture its status and
    // URL explicitly so the report is actionable rather than an empty object.
    if ( error instanceof Response ) {
      const detail = `Route error: HTTP ${error.status} from ${error.url || 'unknown URL'}`;
      console.error( '[RouteError]', detail, error );
      Sentry.captureException( new Error( detail ), {
        tags: { boundary: 'router' },
        extra: { status: error.status, url: error.url }
      });
      return;
    }

    console.error( '[RouteError]', error );
    Sentry.captureException(
      error instanceof Error ? error : new Error( String( error )),
      { tags: { boundary: 'router' }}
    );
  }, [error]);

  return <ErrorPage />;
}
